import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { TimelineButtons } from '../components/TimelineButtons';
import { 
    Syringe, 
    Plus, 
    Calendar as CalendarIcon, 
    Building2, 
    User, 
    AlertTriangle,
    CheckCircle2,
    Clock,
    Trash2,
    Edit2,
    ChevronDown
} from 'lucide-react';
import AttachmentExpander from '../components/AttachmentExpander';
import useAttachmentCounts from '../hooks/useAttachmentCounts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INJECTION_SITES = [
    "Left Arm",
    "Right Arm",
    "Left Thigh",
    "Right Thigh",
    "Other"
];

const REACTIONS = [
    { value: "none", label: "No Reaction", color: "bg-green-500" },
    { value: "mild", label: "Mild (soreness, fatigue)", color: "bg-yellow-500" },
    { value: "moderate", label: "Moderate (fever, body aches)", color: "bg-orange-500" },
    { value: "severe", label: "Severe (seek medical attention)", color: "bg-red-500" }
];

export default function VaccineTracker() {
    const { token } = useAuth();
    const [vaccines, setVaccines] = useState([]);
    const vaccineCounts = useAttachmentCounts('vaccine', vaccines.map(v => v.id));
    const [commonVaccines, setCommonVaccines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [editingVaccine, setEditingVaccine] = useState(null);
    
    // Form state
    const [formData, setFormData] = useState({
        vaccine_name: '',
        date_administered: new Date(),
        manufacturer: '',
        lot_number: '',
        site: '',
        administered_by: '',
        location: '',
        dose_number: '',
        reaction: 'none',
        reaction_details: '',
        next_dose_due: null,
        notes: ''
    });

    const resetForm = () => {
        setFormData({
            vaccine_name: '',
            date_administered: new Date(),
            manufacturer: '',
            lot_number: '',
            site: '',
            administered_by: '',
            location: '',
            dose_number: '',
            reaction: 'none',
            reaction_details: '',
            next_dose_due: null,
            notes: ''
        });
        setEditingVaccine(null);
    };

    const fetchVaccines = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/api/vaccines`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                console.log('[VaccineTracker] Fetched vaccines:', data.length);
                setVaccines(data);
            }
        } catch (error) {
            console.error('[VaccineTracker] Failed to fetch vaccines:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    const fetchCommonVaccines = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/api/vaccines/common`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setCommonVaccines(data);
            }
        } catch (error) {
            console.error('[VaccineTracker] Failed to fetch common vaccines:', error);
        }
    }, [token]);

    useEffect(() => {
        fetchVaccines();
        fetchCommonVaccines();
    }, [fetchVaccines, fetchCommonVaccines]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.vaccine_name || !formData.date_administered) {
            toast.error('Please fill in required fields');
            return;
        }

        try {
            const payload = {
                ...formData,
                date_administered: format(formData.date_administered, 'yyyy-MM-dd'),
                dose_number: formData.dose_number ? parseInt(formData.dose_number) : null,
                next_dose_due: formData.next_dose_due ? format(formData.next_dose_due, 'yyyy-MM-dd') : null
            };

            const url = editingVaccine 
                ? `${API_URL}/api/vaccines/${editingVaccine.id}`
                : `${API_URL}/api/vaccines`;
            
            const res = await fetch(url, {
                method: editingVaccine ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                toast.success(editingVaccine ? 'Vaccine record updated!' : 'Vaccine record added!');
                fetchVaccines();
                setShowAddDialog(false);
                resetForm();
            } else {
                throw new Error('Failed to save');
            }
        } catch (error) {
            console.error('[VaccineTracker] Error saving:', error);
            toast.error('Failed to save vaccine record');
        }
    };

    const handleDelete = async (vaccineId) => {
        if (!window.confirm('Are you sure you want to delete this vaccine record?')) return;

        try {
            const res = await fetch(`${API_URL}/api/vaccines/${vaccineId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                toast.success('Vaccine record deleted');
                fetchVaccines();
            }
        } catch (error) {
            toast.error('Failed to delete record');
        }
    };

    const handleEdit = (vaccine) => {
        setFormData({
            vaccine_name: vaccine.vaccine_name,
            date_administered: parseISO(vaccine.date_administered),
            manufacturer: vaccine.manufacturer || '',
            lot_number: vaccine.lot_number || '',
            site: vaccine.site || '',
            administered_by: vaccine.administered_by || '',
            location: vaccine.location || '',
            dose_number: vaccine.dose_number?.toString() || '',
            reaction: vaccine.reaction || 'none',
            reaction_details: vaccine.reaction_details || '',
            next_dose_due: vaccine.next_dose_due ? parseISO(vaccine.next_dose_due) : null,
            notes: vaccine.notes || ''
        });
        setEditingVaccine(vaccine);
        setShowAddDialog(true);
    };

    const getReactionBadge = (reaction) => {
        const reactionInfo = REACTIONS.find(r => r.value === reaction) || REACTIONS[0];
        return (
            <Badge className={`${reactionInfo.color} text-white`}>
                {reactionInfo.label.split(' ')[0]}
            </Badge>
        );
    };

    // Group vaccines by name for summary
    const vaccineGroups = vaccines.reduce((acc, v) => {
        if (!acc[v.vaccine_name]) {
            acc[v.vaccine_name] = [];
        }
        acc[v.vaccine_name].push(v);
        return acc;
    }, {});

    const upcomingDoses = vaccines.filter(v => v.next_dose_due && new Date(v.next_dose_due) > new Date());

    return (
        <div className="space-y-6" data-testid="vaccine-tracker">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Syringe className="w-7 h-7 text-indigo-500" />
                        Vaccine Tracker
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Track your vaccination history and upcoming doses
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <TimelineButtons />
                    <Dialog open={showAddDialog} onOpenChange={(open) => {
                    setShowAddDialog(open);
                    if (!open) resetForm();
                }}>
                    <DialogTrigger asChild>
                        <Button className="gap-2" data-testid="add-vaccine-btn">
                            <Plus className="w-4 h-4" />
                            Add Vaccine Record
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>
                                {editingVaccine ? 'Edit Vaccine Record' : 'Add Vaccine Record'}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Vaccine Name */}
                            <div className="space-y-2">
                                <Label>Vaccine Name *</Label>
                                <Select 
                                    value={formData.vaccine_name}
                                    onValueChange={(v) => setFormData({...formData, vaccine_name: v})}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select vaccine" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {commonVaccines.map(v => (
                                            <SelectItem key={v} value={v}>{v}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {formData.vaccine_name === 'Other' && (
                                    <Input 
                                        placeholder="Enter vaccine name"
                                        value={formData.vaccine_name === 'Other' ? '' : formData.vaccine_name}
                                        onChange={(e) => setFormData({...formData, vaccine_name: e.target.value})}
                                    />
                                )}
                            </div>

                            {/* Date Administered */}
                            <div className="space-y-2">
                                <Label>Date Administered *</Label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                            {format(formData.date_administered, 'PPP')}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0">
                                        <Calendar
                                            mode="single"
                                            selected={formData.date_administered}
                                            onSelect={(date) => date && setFormData({...formData, date_administered: date})}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>

                            {/* Dose Number & Site */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Dose Number</Label>
                                    <Select 
                                        value={formData.dose_number}
                                        onValueChange={(v) => setFormData({...formData, dose_number: v})}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="1">1st Dose</SelectItem>
                                            <SelectItem value="2">2nd Dose</SelectItem>
                                            <SelectItem value="3">3rd Dose</SelectItem>
                                            <SelectItem value="4">4th Dose (Booster)</SelectItem>
                                            <SelectItem value="5">5th Dose</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Injection Site</Label>
                                    <Select 
                                        value={formData.site}
                                        onValueChange={(v) => setFormData({...formData, site: v})}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {INJECTION_SITES.map(s => (
                                                <SelectItem key={s} value={s}>{s}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Manufacturer & Lot Number */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Manufacturer</Label>
                                    <Input 
                                        placeholder="e.g., Pfizer, Moderna"
                                        value={formData.manufacturer}
                                        onChange={(e) => setFormData({...formData, manufacturer: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Lot Number</Label>
                                    <Input 
                                        placeholder="Lot #"
                                        value={formData.lot_number}
                                        onChange={(e) => setFormData({...formData, lot_number: e.target.value})}
                                    />
                                </div>
                            </div>

                            {/* Location & Administered By */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Location</Label>
                                    <Input 
                                        placeholder="Clinic or pharmacy name"
                                        value={formData.location}
                                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Administered By</Label>
                                    <Input 
                                        placeholder="Doctor or pharmacist"
                                        value={formData.administered_by}
                                        onChange={(e) => setFormData({...formData, administered_by: e.target.value})}
                                    />
                                </div>
                            </div>

                            {/* Reaction */}
                            <div className="space-y-2">
                                <Label>Any Reaction?</Label>
                                <Select 
                                    value={formData.reaction}
                                    onValueChange={(v) => setFormData({...formData, reaction: v})}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {REACTIONS.map(r => (
                                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {formData.reaction !== 'none' && (
                                <div className="space-y-2">
                                    <Label>Reaction Details</Label>
                                    <Textarea 
                                        placeholder="Describe the reaction..."
                                        value={formData.reaction_details}
                                        onChange={(e) => setFormData({...formData, reaction_details: e.target.value})}
                                    />
                                </div>
                            )}

                            {/* Next Dose Due */}
                            <div className="space-y-2">
                                <Label>Next Dose Due (optional)</Label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                            {formData.next_dose_due 
                                                ? format(formData.next_dose_due, 'PPP')
                                                : 'Select date'
                                            }
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0">
                                        <Calendar
                                            mode="single"
                                            selected={formData.next_dose_due}
                                            onSelect={(date) => setFormData({...formData, next_dose_due: date})}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>

                            {/* Notes */}
                            <div className="space-y-2">
                                <Label>Notes</Label>
                                <Textarea 
                                    placeholder="Any additional notes..."
                                    value={formData.notes}
                                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                />
                            </div>

                            <Button type="submit" className="w-full">
                                {editingVaccine ? 'Update Record' : 'Save Vaccine Record'}
                            </Button>
                        </form>
                    </DialogContent>
                </Dialog>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
                                <Syringe className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Total Records</p>
                                <p className="text-2xl font-bold">{vaccines.length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                                <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Vaccine Types</p>
                                <p className="text-2xl font-bold">{Object.keys(vaccineGroups).length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                                <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Upcoming Doses</p>
                                <p className="text-2xl font-bold">{upcomingDoses.length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Upcoming Doses Alert */}
            {upcomingDoses.length > 0 && (
                <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-800">
                    <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                            <div>
                                <h3 className="font-semibold text-amber-800 dark:text-amber-200">Upcoming Doses</h3>
                                <div className="mt-2 space-y-1">
                                    {upcomingDoses.slice(0, 3).map(v => (
                                        <p key={v.id} className="text-sm text-amber-700 dark:text-amber-300">
                                            {v.vaccine_name} - Due {format(parseISO(v.next_dose_due), 'MMM d, yyyy')}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Vaccine Records */}
            <Card>
                <CardHeader>
                    <CardTitle>Vaccine History</CardTitle>
                    <CardDescription>All your vaccination records</CardDescription>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        </div>
                    ) : vaccines.length === 0 ? (
                        <div className="text-center py-12">
                            <Syringe className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                            <h3 className="font-medium text-lg mb-2">No vaccine records yet</h3>
                            <p className="text-muted-foreground mb-4">
                                Start tracking your vaccination history
                            </p>
                            <Button onClick={() => setShowAddDialog(true)}>
                                <Plus className="w-4 h-4 mr-2" />
                                Add First Record
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {vaccines.map(vaccine => (
                                <div 
                                    key={vaccine.id}
                                    className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                                    data-testid={`vaccine-record-${vaccine.id}`}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h4 className="font-semibold">{vaccine.vaccine_name}</h4>
                                                {vaccine.dose_number && (
                                                    <Badge variant="outline">Dose {vaccine.dose_number}</Badge>
                                                )}
                                                {getReactionBadge(vaccine.reaction)}
                                            </div>
                                            <p className="text-sm text-muted-foreground">
                                                {format(parseISO(vaccine.date_administered), 'MMMM d, yyyy')}
                                            </p>
                                            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                                                {vaccine.manufacturer && (
                                                    <span className="flex items-center gap-1">
                                                        <Building2 className="w-3 h-3" />
                                                        {vaccine.manufacturer}
                                                    </span>
                                                )}
                                                {vaccine.location && (
                                                    <span className="flex items-center gap-1">
                                                        <Building2 className="w-3 h-3" />
                                                        {vaccine.location}
                                                    </span>
                                                )}
                                                {vaccine.administered_by && (
                                                    <span className="flex items-center gap-1">
                                                        <User className="w-3 h-3" />
                                                        {vaccine.administered_by}
                                                    </span>
                                                )}
                                            </div>
                                            {vaccine.reaction_details && (
                                                <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                                                    Reaction: {vaccine.reaction_details}
                                                </p>
                                            )}
                                            {vaccine.next_dose_due && (
                                                <p className="mt-1 text-sm text-indigo-600 dark:text-indigo-400">
                                                    Next dose: {format(parseISO(vaccine.next_dose_due), 'MMM d, yyyy')}
                                                </p>
                                            )}
                                            <AttachmentExpander
                                                parentType="vaccine"
                                                parentId={vaccine.id}
                                                initialCount={vaccineCounts[`vaccine:${vaccine.id}`] || 0}
                                            />
                                        </div>
                                        <div className="flex gap-2">
                                            <Button 
                                                variant="ghost" 
                                                size="icon"
                                                onClick={() => handleEdit(vaccine)}
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </Button>
                                            <Button 
                                                variant="ghost" 
                                                size="icon"
                                                onClick={() => handleDelete(vaccine.id)}
                                                className="text-destructive hover:text-destructive"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
